<template>
    <div ref="portalContainer">
        <PanelTransition name="fade">
            <div
                v-if="isOpen"
                class="fixed inset-0 flex items-center justify-center bg-black/50 p-4"
                :style="backdropStyle"
                @click.self="$emit('close')"
            >
                <div
                    ref="focusTrapContainer"
                    role="dialog"
                    aria-modal="true"
                    :aria-label="title"
                    class="flex w-full flex-col overflow-hidden rounded-[14px] border border-theme-border bg-theme-bg shadow-lg"
                    :class="containerClass"
                    :style="containerStyle"
                >
                    <!-- Header -->
                    <div class="flex shrink-0 items-center justify-between border-b border-theme-border px-4 py-3">
                        <slot name="header">
                            <h4 class="m-0 text-base font-semibold text-theme-text">{{ title }}</h4>
                        </slot>
                        <button
                            type="button"
                            :aria-label="localizations[lang].tooltips.close"
                            class="flex h-7 w-7 cursor-pointer items-center justify-center rounded-full border-none bg-transparent text-theme-icon opacity-50 hover:opacity-100 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                            @click="$emit('close')"
                        >
                            <svg-icon :icon="closeIcon" width="20" height="20" />
                        </button>
                    </div>

                    <!-- Content (scrollable) -->
                    <div class="overflow-y-auto">
                        <slot />
                    </div>
                </div>
            </div>
        </PanelTransition>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import PanelTransition from './PanelTransition.vue';
import focusTrapMixin from '../../js/mixins/focusTrapMixin';

export default {
    name: 'MobileModal',
    components: {
        PanelTransition,
    },
    mixins: [focusTrapMixin],
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        title: {
            type: String,
            default: '',
        },
        /**
         * Maximum width of the modal container.
         */
        maxWidth: {
            type: String,
            default: '360px',
        },
        /**
         * Maximum height of the modal container.
         */
        maxHeight: {
            type: String,
            default: '70vh',
        },
        /**
         * Z-index for the modal backdrop and container.
         */
        zIndex: {
            type: [Number, String],
            default: 700,
        },
        /**
         * Icon name for the close button.
         */
        closeIcon: {
            type: String,
            default: 'close',
        },
        /**
         * CSS selector or HTMLElement for portal target.
         * When set, the modal is moved to this element for proper theme inheritance.
         */
        portalTarget: {
            type: [String, HTMLElement],
            default: null,
        },
        /**
         * Additional classes for the modal container.
         */
        containerClass: {
            type: [String, Array, Object],
            default: null,
        },
    },
    emits: ['close'],
    data() {
        return {
            isAttached: false,
            focusTrapEnabled: false,
        };
    },
    computed: {
        ...mapState(['localizations', 'lang']),
        backdropStyle() {
            return {
                zIndex: this.zIndex,
            };
        },
        containerStyle() {
            return {
                maxWidth: this.maxWidth,
                maxHeight: this.maxHeight,
            };
        },
    },
    watch: {
        isOpen: {
            immediate: true,
            handler(isOpening) {
                this.focusTrapEnabled = isOpening;
                if (isOpening && this.portalTarget) {
                    this.$nextTick(() => this.attachPortal());
                }
            },
        },
    },
    beforeDestroy() {
        this.detachPortal();
    },
    methods: {
        onFocusTrapEscape() {
            this.$emit('close');
        },
        /**
         * Attaches the portal container to the target element.
         * Uses portalTarget to ensure theme CSS variables are inherited.
         */
        attachPortal() {
            if (this.isAttached || !this.portalTarget) return;

            const container = this.$refs.portalContainer;
            if (!container) return;

            const target = this.resolvePortalTarget();
            if (target && container.parentNode !== target) {
                target.appendChild(container);
                this.isAttached = true;
            }
        },
        /**
         * Detaches the portal container from the DOM.
         */
        detachPortal() {
            const container = this.$refs.portalContainer;
            if (container && this.isAttached && container.parentNode) {
                container.parentNode.removeChild(container);
                this.isAttached = false;
            }
        },
        /**
         * Resolves the portal target from string selector or HTMLElement.
         * @returns {HTMLElement|null}
         */
        resolvePortalTarget() {
            if (typeof this.portalTarget === 'string') {
                return document.querySelector(this.portalTarget);
            }
            return this.portalTarget;
        },
    },
};
</script>
